import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSession } from '../session/session-context.js';
import { useTheme } from './theme.js';
import { AdminLogo } from './admin-logo.js';
import { Icon, RolePill } from '../ui/index.js';
import type { AdminRole } from '../ui/index.js';

const ROLE_LABEL: Record<string, string> = {
  superadmin: 'Süper Admin',
  super_admin: 'Süper Admin',
  admin: 'Yönetici',
  support: 'Destek',
  support_readonly: 'Destek (Salt Okunur)',
};

function describeRole(role: string | null): string {
  if (!role) return 'misafir';
  return ROLE_LABEL[role] ?? role;
}

function initialsFromUsername(username: string | null): string {
  if (!username) return '?';
  const parts = username.split(/[\s._-]+/).filter(Boolean);
  if (parts.length >= 2 && parts[0] && parts[1]) {
    return (parts[0][0]! + parts[1][0]!).toUpperCase();
  }
  return username.slice(0, 2).toUpperCase();
}

export interface TopbarProps {
  readonly onToggleSidebar?: () => void;
  /**
   * 2026-09-22 — active alarm count from the shell's /admin/overview/alarms
   * poll. `null` = unknown (no capability / endpoint unavailable): no dot.
   */
  readonly alarmCount?: number | null;
  /** At least one active alarm is critical (red dot instead of amber). */
  readonly alarmCritical?: boolean;
  /** The latest alarms poll failed: the count is the last known one, not current. */
  readonly alarmStale?: boolean;
  /**
   * 2026-09-28 (D-243) — open incidents that are not "unread" (acknowledged,
   * snoozed, warn): a grey badge when nothing is unread.
   */
  readonly alarmQuietCount?: number | null;
  /**
   * 2026-09-28 (D-243) — the bell opens this incident list (Okundu /
   * İncelendi / Ertele) instead of navigating; absent → the bell opens the
   * overview as before.
   */
  readonly bellPanel?: (close: () => void) => ReactNode;
}

export function Topbar({
  onToggleSidebar,
  alarmCount = null,
  alarmCritical = false,
  alarmStale = false,
  alarmQuietCount = null,
  bellPanel,
}: TopbarProps = {}): JSX.Element {
  const session = useSession();
  const theme = useTheme();
  const navigate = useNavigate();
  const [now, setNow] = useState(() => Date.now());
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const [bellOpen, setBellOpen] = useState(false);
  const bellRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!bellOpen) return;
    const onDown = (e: MouseEvent) => {
      if (bellRef.current && !bellRef.current.contains(e.target as Node)) setBellOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setBellOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [bellOpen]);

  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 10_000);
    return () => window.clearInterval(t);
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [menuOpen]);

  const expiryMsLeft = session.expiresAt ? session.expiresAt.getTime() - now : 0;
  const expiryLabel = session.expiresAt ? formatRelative(expiryMsLeft) : '—';
  const expiryLow = expiryMsLeft > 0 && expiryMsLeft < 5 * 60_000;

  const themeIsDark = theme.theme === 'dark';
  const initials = initialsFromUsername(session.username);
  const adminRole: AdminRole = (session.role ?? 'admin') as AdminRole;

  return (
    <header className="adm-header">
      <div className="brand-slot">
        <button
          type="button"
          className="iconbtn hide-desktop"
          onClick={onToggleSidebar}
          aria-label="Menüyü aç"
          style={{ marginRight: 4 }}
        >
          <Icon.Menu />
        </button>
        <button
          type="button"
          className="iconbtn"
          onClick={() => navigate('/')}
          aria-label="Genel bakışa git"
          style={{ width: 'auto', padding: '0 4px' }}
        >
          <AdminLogo />
        </button>
      </div>

      {/* Global ⌘K-style search slot. Wired only as a visual placeholder
          here; a real search modal is a future task — the input still
          accepts focus + typing so it doesn't feel inert. */}
      <div className="flex-row gap-3 hide-mobile adm-search" style={{ flex: 1, maxWidth: 480 }}>
        <div className="input-group" style={{ flex: 1, position: 'relative' }}>
          <Icon.Search className="icon-left" size={14} />
          <input
            className="input"
            placeholder="Kullanıcı, sipariş, parent ID ara…"
            style={{ height: 34, paddingRight: 56 }}
            aria-label="Arama"
          />
          <span
            className="kbd"
            style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)' }}
          >
            ⌘K
          </span>
        </div>
      </div>

      <div className="grow" />

      <div className="flex-row gap-2" style={{ alignItems: 'center', gap: 8 }}>
        {/* ≤880 (no room in the header): hidden, except in its last 5 minutes
            (session-chip--low), when it floats as a small pill under the top
            bar; the account menu always lists the remaining time there. */}
        <span
          className={`session-chip hide-mobile${expiryLow ? ' session-chip--low' : ''}`}
          title="Oturum süresi"
          data-testid="topbar-expiry"
          aria-label="oturum süresi"
        >
          <Icon.Clock size={12} />
          <span style={{ color: 'var(--text-tertiary)' }}>Oturum:</span>
          <strong style={{ color: expiryLow ? 'var(--danger-600)' : 'var(--text-primary)' }}>
            {expiryLabel}
          </strong>
        </span>

        <span className="hide-mobile">
          <RolePill role={adminRole} />
        </span>

        <button
          type="button"
          className="iconbtn theme-toggle"
          onClick={theme.toggleTheme}
          aria-label={themeIsDark ? 'açık temaya geç' : 'koyu temaya geç'}
          aria-pressed={themeIsDark}
          title={themeIsDark ? 'Açık temaya geç' : 'Koyu temaya geç'}
          data-testid="theme-toggle"
        >
          {themeIsDark ? <Icon.Sun /> : <Icon.Moon />}
        </button>

        {/* 2026-09-22 — the bell used to carry a permanent fake red dot. It
            shows the real count (shell poll); no dot when nothing is active or
            the state is unknown. 2026-09-28 (D-243): the red count is the
            UNREAD critical incidents only (acknowledged / snoozed ones get a
            grey badge) and the bell opens the incident list with Okundu /
            İncelendi / Ertele. */}
        <div ref={bellRef} className="topbar-bell-wrap" style={{ position: 'relative' }}>
          <button
            type="button"
            className="iconbtn hide-narrow"
            title={
              alarmCount === null
                ? 'Bildirimler'
                : alarmStale
                  ? `Alarm durumu okunamadı — son bilinen: ${alarmCount} okunmamış kritik olay`
                  : alarmCount > 0
                    ? `${alarmCount} okunmamış kritik olay — listeyi aç`
                    : alarmQuietCount !== null && alarmQuietCount > 0
                      ? `Okunmamış kritik olay yok · ${alarmQuietCount} açık olay`
                      : 'Aktif alarm yok'
            }
            data-stale={alarmStale ? 'true' : undefined}
            aria-label={
              alarmCount !== null && alarmCount > 0 ? `bildirimler: ${alarmCount} okunmamış kritik olay` : 'bildirimler'
            }
            aria-haspopup={bellPanel ? 'dialog' : undefined}
            aria-expanded={bellPanel ? bellOpen : undefined}
            data-testid="topbar-bell"
            onClick={() => (bellPanel ? setBellOpen((o) => !o) : navigate('/'))}
            style={{ position: 'relative' }}
          >
            <Icon.Bell />
            {alarmCount !== null && alarmCount > 0 ? (
              <span
                aria-hidden="true"
                data-testid="topbar-bell-count"
                style={{
                  position: 'absolute',
                  top: 3,
                  right: 2,
                  minWidth: 15,
                  height: 15,
                  padding: '0 4px',
                  borderRadius: 999,
                  background: alarmCritical ? 'var(--danger-500)' : 'var(--warn-500)',
                  color: '#fff',
                  fontSize: 9.5,
                  fontWeight: 700,
                  lineHeight: '15px',
                  textAlign: 'center',
                  boxShadow: '0 0 0 2px var(--bg-elevated)',
                }}
              >
                {alarmCount > 9 ? '9+' : alarmCount}
              </span>
            ) : alarmQuietCount !== null && alarmQuietCount > 0 ? (
              <span
                aria-hidden="true"
                data-testid="topbar-bell-quiet"
                style={{
                  position: 'absolute',
                  top: 3,
                  right: 2,
                  minWidth: 15,
                  height: 15,
                  padding: '0 4px',
                  borderRadius: 999,
                  background: 'var(--gray-400)',
                  color: '#fff',
                  fontSize: 9.5,
                  fontWeight: 700,
                  lineHeight: '15px',
                  textAlign: 'center',
                  boxShadow: '0 0 0 2px var(--bg-elevated)',
                }}
              >
                {alarmQuietCount > 9 ? '9+' : alarmQuietCount}
              </span>
            ) : null}
          </button>
          {bellPanel && bellOpen ? (
            <div className="menu topbar-incidents-menu" style={{ right: 0, top: 'calc(100% + 6px)' }}>
              {bellPanel(() => setBellOpen(false))}
            </div>
          ) : null}
        </div>

        <div ref={menuRef} style={{ position: 'relative' }}>
          <button
            type="button"
            className="iconbtn"
            onClick={() => setMenuOpen((o) => !o)}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-label="hesap menüsü"
            style={{ width: 'auto', padding: '0 8px', gap: 8 }}
          >
            <span
              style={{
                width: 26,
                height: 26,
                borderRadius: 999,
                background: 'linear-gradient(135deg,#23262d,#0b0c10)',
                color: 'white',
                fontSize: 11,
                fontWeight: 600,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              aria-hidden="true"
            >
              {initials}
            </span>
            {/* Username is shown inside the dropdown menu (initials-only
                in the trigger per mockup admin-shell.jsx:121). We keep
                the data-testid here on a visually-hidden span so the
                topbar-actor test contract is preserved. */}
            <span className="sr-only" data-testid="topbar-actor">
              {session.username ?? '—'}
            </span>
            <Icon.Chevron size={12} />
          </button>
          {menuOpen ? (
            <div className="menu" style={{ right: 0, top: 'calc(100% + 6px)', minWidth: 260 }}>
              <div style={{ padding: '10px 10px' }}>
                <div style={{ fontSize: 13.5, fontWeight: 600 }}>
                  {session.username ?? 'Operatör'}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 2 }}>
                  @{session.username ?? '—'}
                </div>
                <div style={{ marginTop: 8 }}>
                  <RolePill role={adminRole} />
                </div>
                {/* Shown ≤880 only (the header chip is hidden there). */}
                <div className="menu-session">
                  <Icon.Clock size={12} />
                  <span>Oturum:</span>
                  <strong style={{ color: expiryLow ? 'var(--danger-600)' : 'var(--text-primary)' }}>{expiryLabel}</strong>
                </div>
                {session.actorId ? (
                  <div
                    style={{
                      marginTop: 8,
                      fontSize: 11,
                      color: 'var(--text-tertiary)',
                      fontFamily: 'var(--font-mono)',
                    }}
                  >
                    {session.actorId}
                  </div>
                ) : null}
              </div>
              <div className="menu-divider" />
              <button
                type="button"
                className="menu-item"
                onClick={() => {
                  setMenuOpen(false);
                  navigate('/');
                }}
              >
                <Icon.Activity />
                Genel Bakış
              </button>
              <button
                type="button"
                className="menu-item"
                onClick={() => {
                  setMenuOpen(false);
                  navigate('/activity');
                }}
              >
                <Icon.Clock />
                Son Aktiviteler
              </button>
              <div className="menu-divider" />
              <button
                type="button"
                className="menu-item"
                onClick={() => {
                  setMenuOpen(false);
                  session.logout();
                }}
                aria-label="çıkış"
              >
                <Icon.Logout />
                Çıkış yap
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </header>
  );
}

function formatRelative(msRemaining: number): string {
  if (msRemaining <= 0) return 'süresi doldu';
  const totalSeconds = Math.floor(msRemaining / 1000);
  if (totalSeconds < 60) return `${totalSeconds} sn kaldı`;
  const minutes = Math.floor(totalSeconds / 60);
  if (minutes < 60) return `${minutes} dk kaldı`;
  const hours = Math.floor(minutes / 60);
  const remMin = minutes % 60;
  return remMin === 0 ? `${hours} sa kaldı` : `${hours} sa ${remMin} dk kaldı`;
}
